/**
 * Offline outbox — a small IndexedDB store for shop-chat messages written while
 * the network was gone. The composer holds a message here on failure;
 * `drainOutbox` sends it when we're back.
 *
 * Scope:
 *   - shop-chat messages, text and attachment references alike
 *   - photo bytes are not held; a photo has to have been uploaded already
 *   - retries are capped in the drain layer, not here (MAX_SEND_ATTEMPTS)
 *
 * Every transaction below settles on abort as well as error. That is not
 * defensive habit: `enqueue` is awaited inside the composer's try/finally, so a
 * promise that never settles leaves `sending` true and the Send button greyed
 * out for the rest of the shift, with nothing on screen to explain it. The
 * browser aborts without an error whenever it evicts origin storage or site
 * data gets cleared, both of which happen on a managed shop tablet.
 */

const DB_NAME = 'fleetwise-offline';
const STORE = 'outbox';
const VERSION = 1;

export interface OutboxItem {
  id: string;
  /** Which surface sent it — for routing on drain. */
  channel: 'shopchat' | 'diagnose';
  /**
   * Who wrote it. The store is scoped to the device, not the person, and the
   * shop tablet changes hands all shift — the roster swaps sessions in place
   * with no reload. Without this, the next tech to tap their name would send
   * the previous tech's held message, under their own name, from whatever
   * screen they happened to land on. A message waits for its author.
   */
  userId: string;
  /** Serialized payload for the tRPC call. */
  payload: unknown;
  createdAt: number;
  /**
   * Rejections only. A message that can't leave because there's no network has
   * not failed — it's waiting, and waiting must not spend a budget. Only the
   * server actively refusing it counts against MAX_SEND_ATTEMPTS.
   */
  attempts: number;
  /**
   * Set when the send succeeded but we couldn't delete the row afterwards.
   * It must never be sent twice, so the next pass only retries the delete.
   */
  deliveredAt?: number;
  /** The tech's own words, so a message we've given up on can be read back. */
  preview: string;
}

/**
 * One connection for the app, not one per call. The banner reads the queue on
 * every change, so opening fresh each time meant thousands of connections over
 * a shift, none of them closed — and an always-open connection is also what
 * would block a future version bump from ever completing.
 */
let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('This browser has no offline storage.'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onerror = () => reject(req.error ?? new Error('indexeddb open failed'));
    // Another tab is holding the old version open. Say so instead of hanging.
    req.onblocked = () =>
      reject(new Error('Fleetwise is open in another tab — close it and retry.'));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Step aside so another tab's upgrade can proceed, and drop the cached
      // handle whenever this connection goes away so the next call reopens.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      db.onclose = () => {
        dbPromise = null;
      };
      resolve(db);
    };
  }).catch((err: unknown) => {
    // Don't cache a failure — a tablet that recovers storage should recover here.
    dbPromise = null;
    throw err;
  });
  return dbPromise;
}

function runTransaction<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore, capture: (value: T) => void) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    let captured: T | undefined;
    // Throws InvalidStateError when this connection is already closing.
    const tx = db.transaction(STORE, mode);
    tx.oncomplete = () => resolve(captured as T);
    tx.onerror = () => reject(tx.error ?? new Error('outbox transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('outbox transaction aborted'));
    work(tx.objectStore(STORE), (value) => {
      captured = value;
    });
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore, capture: (value: T) => void) => void,
): Promise<T> {
  try {
    return await runTransaction<T>(await open(), mode, work);
  } catch {
    // Caching the connection lost a property the old open-every-call code had
    // for free: self-healing. A connection the browser force-closed (storage
    // eviction, "clear site data" on a managed tablet) throws on every
    // transaction, and the cached handle would keep serving it for the life of
    // the tab — every send failing, the queue unreadable, only a reload out of
    // it. Drop it and try once with a fresh one.
    dbPromise = null;
    return runTransaction<T>(await open(), mode, work);
  }
}

/** Fires whenever the queue changes, so the banner reflects it without polling. */
type OutboxListener = () => void;
const listeners = new Set<OutboxListener>();

export function subscribeOutbox(fn: OutboxListener): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function notify(): void {
  for (const fn of listeners) {
    try {
      fn();
    } catch {
      // A listener that throws must not break the write that triggered it.
    }
  }
}

export async function enqueue(
  item: Omit<OutboxItem, 'id' | 'createdAt' | 'attempts'>,
): Promise<OutboxItem> {
  const full: OutboxItem = {
    ...item,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    attempts: 0,
  };
  await withStore<void>('readwrite', (store) => {
    store.put(full);
  });
  notify();
  return full;
}

/** Records a send that landed but whose row we then failed to delete. */
export async function markDelivered(id: string): Promise<void> {
  await withStore<void>('readwrite', (store) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const item = get.result as OutboxItem | undefined;
      if (!item) return;
      item.deliveredAt = Date.now();
      store.put(item);
    };
  });
  notify();
}

export async function listOutbox(): Promise<OutboxItem[]> {
  return withStore<OutboxItem[]>('readonly', (store, capture) => {
    const req = store.getAll();
    req.onsuccess = () =>
      capture((req.result as OutboxItem[]).sort((a, b) => a.createdAt - b.createdAt));
  });
}

export async function removeFromOutbox(id: string): Promise<void> {
  await withStore<void>('readwrite', (store) => {
    store.delete(id);
  });
  notify();
}

export async function bumpAttempts(id: string): Promise<void> {
  await withStore<void>('readwrite', (store) => {
    const get = store.get(id);
    get.onsuccess = () => {
      const item = get.result as OutboxItem | undefined;
      if (!item) return;
      item.attempts += 1;
      store.put(item);
    };
  });
  notify();
}
